import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Button, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, Grid, IconButton, Chip,
  FormControl, InputLabel, Select, MenuItem, Alert,
  CircularProgress, Switch, FormControlLabel, Tooltip
} from '@mui/material';
import { Add, Edit, Delete, Refresh, Security, AdminPanelSettings } from '@mui/icons-material';
import api from '../services/api';
import { toast } from 'react-hot-toast';

const Users = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [permissions, setPermissions] = useState(null);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    first_name: '',
    last_name: '',
    user_type: 'worker',
    phone: '',
    address: '',
    is_active: true
  });

  const USER_TYPES = {
    super_admin: 'Super Admin',
    master_admin: 'Master Admin',
    manager: 'Manager',
    supervisor: 'Supervisor',
    accountant: 'Accountant',
    worker: 'Worker',
    driver: 'Driver'
  };

  const USER_TYPE_COLORS = {
    super_admin: 'error',
    master_admin: 'warning',
    manager: 'primary',
    supervisor: 'info',
    accountant: 'success',
    worker: 'default',
    driver: 'secondary'
  };

  useEffect(() => {
    fetchPermissions();
    fetchUsers();
  }, []);

  const fetchPermissions = async () => {
    try {
      const response = await api.get('/auth/permissions/');
      setPermissions(response.data);
    } catch (error) {
      console.error('Error fetching permissions:', error);
    }
  };

  const fetchUsers = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.get('/auth/users/');
      let data = [];
      if (Array.isArray(response.data)) {
        data = response.data;
      } else if (response.data?.results) {
        data = response.data.results;
      }
      setUsers(data);
    } catch (error) {
      console.error('Error fetching users:', error);
      setError('Failed to fetch users');
      toast.error('Failed to fetch users');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (user = null) => {
    if (user) {
      setEditingUser(user);
      setFormData({
        username: user.username || '',
        email: user.email || '',
        password: '',
        first_name: user.first_name || '',
        last_name: user.last_name || '',
        user_type: user.user_type || 'worker',
        phone: user.phone || '',
        address: user.address || '',
        is_active: user.is_active !== undefined ? user.is_active : true
      });
    } else {
      setEditingUser(null);
      setFormData({
        username: '',
        email: '',
        password: '',
        first_name: '',
        last_name: '',
        user_type: 'worker',
        phone: '',
        address: '',
        is_active: true
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingUser(null);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSwitchChange = (e) => {
    setFormData({ ...formData, is_active: e.target.checked });
  };

  const handleSubmit = async () => {
    try {
      if (!formData.username) {
        toast.error('Username is required');
        return;
      }
      if (!editingUser && !formData.password) {
        toast.error('Password is required for new users');
        return;
      }

      const submitData = { ...formData };
      if (!editingUser) {
        if (!submitData.password) {
          toast.error('Password is required');
          return;
        }
      } else {
        if (!submitData.password) {
          delete submitData.password;
        }
      }

      if (editingUser) {
        await api.put(`/auth/users/${editingUser.id}/`, submitData);
        toast.success('User updated successfully');
      } else {
        await api.post('/auth/register/', submitData);
        toast.success('User created successfully');
      }
      handleCloseDialog();
      fetchUsers();
    } catch (error) {
      console.error('Error saving user:', error);
      if (error.response) {
        const errorMsg = error.response.data?.detail || 
                         error.response.data?.message || 
                         JSON.stringify(error.response.data);
        toast.error(`Failed to save: ${errorMsg}`);
      } else {
        toast.error('Failed to save user');
      }
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this user?')) {
      try {
        await api.delete(`/auth/users/${id}/`);
        toast.success('User deleted successfully');
        fetchUsers();
      } catch (error) {
        toast.error('Failed to delete user');
      }
    }
  };

  const getVisibleUserTypes = () => {
    if (permissions?.user_types_visible) {
      return permissions.user_types_visible;
    }
    return ['manager', 'supervisor', 'accountant', 'worker', 'driver'];
  };

  const getPermissionCount = (userType) => {
    const rolePermissions = {
      super_admin: 'Full Access (All Modules)',
      master_admin: 'Read/Write (All Modules)',
      manager: 'Limited (Inventory, Procurement, Production, Sales, Dispatch)',
      supervisor: 'View Only + Production Create',
      accountant: 'Finance + Reports',
      worker: 'Production + Inventory View',
      driver: 'Dispatch Only'
    };
    return rolePermissions[userType] || 'Limited';
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">User Management</Typography>
        <Box>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={fetchUsers}
            sx={{ mr: 1 }}
          >
            Refresh
          </Button>
          <Button variant="contained" startIcon={<Add />} onClick={() => handleOpenDialog()}>
            Add User
          </Button>
        </Box>
      </Box>

      {permissions && (
        <Alert severity="info" sx={{ mb: 2 }}>
          <strong>Your Role:</strong> {USER_TYPES[permissions.user_type] || permissions.user_type}
          {permissions.can_manage_users && ' | You can manage users'}
          {permissions.can_manage_master_admins && ' | You can create Master Admins'}
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: 2 }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Username</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Role</TableCell>
                <TableCell>Permissions</TableCell>
                <TableCell align="center">Status</TableCell>
                <TableCell align="center">Created By</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {users.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    <Typography color="textSecondary" sx={{ py: 3 }}>
                      No users found
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                users.map((user) => (
                  <TableRow key={user.id}>
                    <TableCell>{user.username}</TableCell>
                    <TableCell>{user.first_name} {user.last_name}</TableCell>
                    <TableCell>{user.email}</TableCell>
                    <TableCell>
                      <Chip 
                        label={USER_TYPES[user.user_type] || user.user_type} 
                        color={USER_TYPE_COLORS[user.user_type] || 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Tooltip title={getPermissionCount(user.user_type)}>
                        <Chip 
                          label={user.user_type === 'super_admin' ? 'Full Access' : 'Limited'}
                          size="small"
                          variant="outlined"
                          icon={<Security />}
                        />
                      </Tooltip>
                    </TableCell>
                    <TableCell align="center">
                      <Chip 
                        label={user.is_active ? 'Active' : 'Inactive'} 
                        color={user.is_active ? 'success' : 'error'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="center">
                      {user.created_by_username || 'Self'}
                    </TableCell>
                    <TableCell align="right">
                      <IconButton size="small" onClick={() => handleOpenDialog(user)}>
                        <Edit />
                      </IconButton>
                      <IconButton size="small" color="error" onClick={() => handleDelete(user.id)}>
                        <Delete />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* User Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingUser ? 'Edit User' : 'Add New User'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Username"
                name="username"
                value={formData.username}
                onChange={handleInputChange}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={editingUser ? "New Password (leave blank to keep current)" : "Password"}
                name="password"
                type="password"
                value={formData.password}
                onChange={handleInputChange}
                required={!editingUser}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="First Name"
                name="first_name"
                value={formData.first_name}
                onChange={handleInputChange}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Last Name"
                name="last_name"
                value={formData.last_name}
                onChange={handleInputChange}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleInputChange}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Role (User Type)</InputLabel>
                <Select
                  name="user_type"
                  value={formData.user_type}
                  onChange={handleInputChange}
                  label="Role (User Type)"
                >
                  {getVisibleUserTypes().map((type) => (
                    <MenuItem key={type} value={type}>
                      {USER_TYPES[type] || type}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Phone"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Address"
                name="address"
                value={formData.address}
                onChange={handleInputChange}
                multiline
                rows={2}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.is_active}
                    onChange={handleSwitchChange}
                    name="is_active"
                    color="primary"
                  />
                }
                label="Active User"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained">
            {editingUser ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Users;